<template>
  <div class="subscriptions-view">
    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h1 class="page-title">Appareils</h1>
        <p class="page-subtitle">Gérer les appareils enregistrés</p>
      </div>
    </div>

    <!-- Dialog -->
    <v-dialog v-model="dialog" scrollable max-width="650px">
      <v-card class="modern-card">
        <v-card-title class="dialog-title">
          <v-icon left color="primary">mdi-cellphone-link</v-icon>
          {{ form_title }}
        </v-card-title>
        <v-card-text class="dialog-content">
          <v-row>
            <v-col cols="12" sm="6">
              <v-select
                :items="businesses"
                item-value="id"
                item-text="name"
                label="Compte"
                @change="setBusinessStores(device.business_id)"
                v-model="device.business_id"
                outlined
                dense
                prepend-inner-icon="mdi-domain"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                label="Appareil"
                v-model="device.name"
                outlined
                dense
                prepend-inner-icon="mdi-cellphone"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                label="Fournisseur"
                v-model="device.manufacturer"
                outlined
                dense
                prepend-inner-icon="mdi-factory"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                label="Modèle"
                v-model="device.model"
                outlined
                dense
                prepend-inner-icon="mdi-information"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                label="Numéro de Série"
                v-model="device.serial_number"
                outlined
                dense
                prepend-inner-icon="mdi-barcode"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-select
                :items="status"
                item-value="id"
                item-text="name"
                label="Statut"
                v-model="device.status"
                outlined
                dense
                prepend-inner-icon="mdi-toggle-switch"
              ></v-select>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn text @click="close" class="cancel-btn">Annuler</v-btn>
          <v-btn color="secondary" @click="save" :loading="loading" :disabled="loading" class="save-btn">
            <v-icon left small>mdi-check</v-icon>
            Enregistrer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Details Modal -->
    <v-dialog v-model="detailsDialog" scrollable max-width="900px">
      <v-card class="details-modal" v-if="selectedDevice">
        <v-card-title class="modal-header">
          <div class="modal-title">
            <v-icon left color="primary">mdi-cellphone-information</v-icon>
            Détails de l'appareil
          </div>
          <v-btn icon @click="detailsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-card-text class="modal-content">
          <!-- Device Info -->
          <div class="detail-section">
            <h3 class="section-title">
              <v-icon left small color="primary">mdi-cellphone</v-icon>
              Appareil
            </h3>
            <v-row class="detail-grid">
              <v-col cols="12" sm="6" md="4">
                <div class="detail-item">
                  <span class="detail-label">Nom</span>
                  <span class="detail-value">{{ selectedDevice.name }}</span>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <div class="detail-item">
                  <span class="detail-label">Type</span>
                  <v-chip small :color="getDeviceTypeColor(selectedDevice.type)" class="white--text">
                    <v-icon left x-small>{{ getDeviceTypeIcon(selectedDevice.type) }}</v-icon>
                    {{ getDeviceTypeLabel(selectedDevice.type) }}
                  </v-chip>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <div class="detail-item">
                  <span class="detail-label">Statut</span>
                  <v-chip small :class="selectedDevice.status ? 'status-active' : 'status-inactive'">
                    {{ getStatusLabel(selectedDevice.status) }}
                  </v-chip>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <div class="detail-item">
                  <span class="detail-label">Numéro de série</span>
                  <span class="detail-value">{{ selectedDevice.serial_number || '-' }}</span>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <div class="detail-item">
                  <span class="detail-label">Identifiant</span>
                  <span class="detail-value">{{ selectedDevice.identifier || '-' }}</span>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <div class="detail-item">
                  <span class="detail-label">Build #</span>
                  <span class="detail-value">{{ selectedDevice.os_version || '-' }}</span>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <div class="detail-item">
                  <span class="detail-label">Fabricant</span>
                  <span class="detail-value">{{ selectedDevice.manufacturer || '-' }}</span>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <div class="detail-item">
                  <span class="detail-label">Modèle</span>
                  <span class="detail-value">{{ selectedDevice.model || '-' }}</span>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <div class="detail-item">
                  <span class="detail-label">Opérateur</span>
                  <span class="detail-value">{{ selectedDevice.operator || '-' }}</span>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <div class="detail-item">
                  <span class="detail-label">Adresse IP</span>
                  <span class="detail-value">{{ selectedDevice.ip_address || '-' }}</span>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <div class="detail-item">
                  <span class="detail-label">Adresse MAC</span>
                  <span class="detail-value">{{ selectedDevice.mac_address || '-' }}</span>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <div class="detail-item">
                  <span class="detail-label">IMEI</span>
                  <span class="detail-value">{{ selectedDevice.imei || '-' }}</span>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <div class="detail-item">
                  <span class="detail-label">Date création</span>
                  <span class="detail-value">{{ formatDateTime(selectedDevice.created_at) }}</span>
                </div>
              </v-col>
            </v-row>
          </div>

          <!-- Business Info -->
          <div class="detail-section" v-if="selectedDevice.business">
            <h3 class="section-title">
              <v-icon left small color="secondary">mdi-domain</v-icon>
              Compte
            </h3>
            <v-row class="detail-grid">
              <v-col cols="12" sm="6" md="4">
                <div class="detail-item">
                  <span class="detail-label">Nom</span>
                  <span class="detail-value">{{ selectedDevice.business.name }}</span>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <div class="detail-item">
                  <span class="detail-label">Contact</span>
                  <span class="detail-value">{{ selectedDevice.business.contact_full_name || '-' }}</span>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <div class="detail-item">
                  <span class="detail-label">Téléphone</span>
                  <span class="detail-value">{{ selectedDevice.business.phone || '-' }}</span>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <div class="detail-item">
                  <span class="detail-label">Email</span>
                  <span class="detail-value">{{ selectedDevice.business.email || '-' }}</span>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <div class="detail-item">
                  <span class="detail-label">Préfixe</span>
                  <span class="detail-value">{{ selectedDevice.business.prefix || '-' }}</span>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <div class="detail-item">
                  <span class="detail-label">Adresse</span>
                  <span class="detail-value">{{ selectedDevice.business.address || '-' }}</span>
                </div>
              </v-col>
            </v-row>
          </div>

          <!-- Subscriptions -->
          <div class="detail-section" v-if="selectedDevice.subscriptions && selectedDevice.subscriptions.length > 0">
            <h3 class="section-title">
              <v-icon left small color="success">mdi-calendar-check</v-icon>
              Souscriptions ({{ selectedDevice.subscriptions.length }})
            </h3>
            
            <div class="subscriptions-list">
              <v-expansion-panels accordion>
                <v-expansion-panel v-for="(sub, index) in selectedDevice.subscriptions" :key="index">
                  <v-expansion-panel-header>
                    <div class="subscription-header">
                      <v-chip x-small :color="getSubscriptionStatusColor(sub.status)" class="white--text mr-2">
                        {{ sub.status }}
                      </v-chip>
                      <span class="sub-plan">{{ sub.plan ? sub.plan.name : 'Plan' }}</span>
                      <span class="sub-dates">{{ formatDate(sub.starts_on) }} - {{ formatDate(sub.ends_on) }}</span>
                      <v-chip x-small :color="getPaymentStatusColor(getSubPaymentStatus(sub))" class="white--text ml-2">
                        {{ getSubPaymentStatus(sub) }}
                      </v-chip>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-row class="detail-grid">
                      <!-- Plan Info -->
                      <v-col cols="12" sm="6" md="4" v-if="sub.plan">
                        <div class="detail-item">
                          <span class="detail-label">Plan</span>
                          <span class="detail-value">{{ sub.plan.name }}</span>
                        </div>
                      </v-col>
                      <v-col cols="12" sm="6" md="4" v-if="sub.plan">
                        <div class="detail-item">
                          <span class="detail-label">Montant du plan</span>
                          <span class="detail-value amount-value">{{ formatNumber(sub.plan.amount) }} {{ sub.plan.currency }}</span>
                        </div>
                      </v-col>
                      <v-col cols="12" sm="6" md="4">
                        <div class="detail-item">
                          <span class="detail-label">Début</span>
                          <span class="detail-value">{{ formatDate(sub.starts_on) }}</span>
                        </div>
                      </v-col>
                      <v-col cols="12" sm="6" md="4">
                        <div class="detail-item">
                          <span class="detail-label">Fin</span>
                          <span class="detail-value">{{ formatDate(sub.ends_on) }}</span>
                        </div>
                      </v-col>
                      
                      <!-- Payment Info -->
                      <v-col cols="12" v-if="sub.payment_request">
                        <v-divider class="my-3"></v-divider>
                        <h4 class="subsection-title">Paiement</h4>
                      </v-col>
                      <v-col cols="12" sm="6" md="4" v-if="sub.payment_request">
                        <div class="detail-item">
                          <span class="detail-label">Commande</span>
                          <span class="detail-value">{{ sub.payment_request.order_number }}</span>
                        </div>
                      </v-col>
                      <v-col cols="12" sm="6" md="4" v-if="sub.payment_request">
                        <div class="detail-item">
                          <span class="detail-label">Montant</span>
                          <span class="detail-value amount-value">{{ formatNumber(sub.payment_request.amount) }} {{ sub.payment_request.currency }}</span>
                        </div>
                      </v-col>
                      <v-col cols="12" sm="6" md="4" v-if="sub.payment_request">
                        <div class="detail-item">
                          <span class="detail-label">Processeur</span>
                          <v-chip x-small :color="getProcessorColor(sub.payment_request.processor_name)" class="white--text">
                            {{ sub.payment_request.processor_name }}
                          </v-chip>
                        </div>
                      </v-col>
                      <v-col cols="12" sm="6" md="4" v-if="sub.payment_request">
                        <div class="detail-item">
                          <span class="detail-label">Statut paiement</span>
                          <div class="payment-status-cell">
                            <v-chip x-small :color="getPaymentStatusColor(sub.payment_request.status)" class="white--text">
                              {{ sub.payment_request.status }}
                            </v-chip>
                            <v-btn
                              v-if="sub.payment_request.status === 'PENDING'"
                              icon
                              x-small
                              color="primary"
                              class="verify-btn ml-1"
                              :loading="verifyingPayments[sub.payment_request.id]"
                              @click="verifyPayment(sub)"
                              title="Vérifier le statut"
                            >
                              <v-icon x-small>mdi-refresh</v-icon>
                            </v-btn>
                          </div>
                        </div>
                      </v-col>
                      <v-col cols="12" sm="6" md="4" v-if="sub.payment_request && sub.payment_request.transaction_id">
                        <div class="detail-item">
                          <span class="detail-label">Transaction ID</span>
                          <span class="detail-value">{{ sub.payment_request.transaction_id }}</span>
                        </div>
                      </v-col>
                      <v-col cols="12" sm="6" md="4" v-if="sub.payment_request">
                        <div class="detail-item">
                          <span class="detail-label">Date paiement</span>
                          <span class="detail-value">{{ formatDateTime(sub.payment_request.created_at) }}</span>
                        </div>
                      </v-col>
                      <v-col cols="12" sm="6" md="4" v-if="sub.payment_request && sub.payment_request.payer">
                        <div class="detail-item">
                          <span class="detail-label">Payeur</span>
                          <span class="detail-value">{{ sub.payment_request.payer }}</span>
                        </div>
                      </v-col>
                      <v-col cols="12" v-if="sub.payment_request && sub.payment_request.message">
                        <div class="detail-item">
                          <span class="detail-label">Message</span>
                          <span class="detail-value">{{ sub.payment_request.message }}</span>
                        </div>
                      </v-col>
                      <v-col cols="12" v-if="isSubscriptionActive(sub)" class="transfer-action-col">
                        <v-divider class="my-3"></v-divider>
                        <v-btn
                          color="primary"
                          outlined
                          small
                          class="transfer-btn"
                          @click.stop="openTransferDialog(sub)"
                        >
                          <v-icon left small>mdi-swap-horizontal</v-icon>
                          Transférer
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>
          </div>

          <div class="detail-section" v-else>
            <h3 class="section-title">
              <v-icon left small color="grey">mdi-calendar-remove</v-icon>
              Souscriptions
            </h3>
            <p class="no-data">Aucune souscription pour cet appareil</p>
          </div>

          <DeviceFeatureSubscriptionsBlock
            v-if="selectedDevice && selectedDevice.serial_number"
            :serial-number="selectedDevice.serial_number"
            :devices-list="rows"
            :current-device-id="selectedDevice.id"
            @transferred="onFeatureSubscriptionTransferred"
          />
        </v-card-text>
        
        <v-card-actions class="modal-actions">
          <v-spacer></v-spacer>
          <v-btn text @click="detailsDialog = false">Fermer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Transfert de souscription (au-dessus du modal détails) -->
    <v-dialog
      v-model="transferDialog"
      max-width="560px"
      scrollable
      content-class="transfer-subscription-dialog"
      @click:outside="closeTransferDialog"
    >
      <v-card v-if="transferSubscription && selectedDevice">
        <v-card-title class="modal-header transfer-modal-header">
          <div class="modal-title">
            <v-icon left color="primary">mdi-swap-horizontal</v-icon>
            Transférer la souscription
          </div>
          <v-btn icon @click="closeTransferDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="transfer-modal-body">
          <div class="detail-section transfer-origin">
            <h4 class="subsection-title">Appareil d’origine</h4>
            <p class="mb-1"><strong>{{ selectedDevice.name }}</strong></p>
            <p class="mb-0 text-caption text--secondary">
              S/N : {{ selectedDevice.serial_number || '—' }}
            </p>
            <p v-if="transferSubscription.plan" class="mt-2 mb-0 text-body-2">
              Plan : {{ transferSubscription.plan.name }}
            </p>
          </div>
          <v-text-field
            v-model="transferDeviceSearch"
            prepend-inner-icon="mdi-magnify"
            label="Rechercher un appareil (nom, série, identifiant)"
            outlined
            dense
            hide-details
            class="mb-3"
            clearable
          ></v-text-field>
          <p class="text-caption text--secondary mb-2">
            Même compte uniquement · {{ filteredTransferDevices.length }} appareil(s)
          </p>
          <v-list dense class="transfer-device-list" v-if="filteredTransferDevices.length">
            <v-list-item
              v-for="d in filteredTransferDevices"
              :key="d.id"
              @click="transferTargetDevice = d"
              :class="{ 'transfer-device-selected': transferTargetDevice && transferTargetDevice.id === d.id }"
            >
              <v-list-item-content>
                <v-list-item-title>{{ d.name }}</v-list-item-title>
                <v-list-item-subtitle>
                  S/N : {{ d.serial_number || '—' }}
                  <span v-if="d.identifier"> · {{ d.identifier }}</span>
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action v-if="transferTargetDevice && transferTargetDevice.id === d.id">
                <v-icon color="primary">mdi-check-circle</v-icon>
              </v-list-item-action>
            </v-list-item>
          </v-list>
          <p v-else class="no-data py-4">Aucun autre appareil pour ce compte.</p>
        </v-card-text>
        <v-card-actions class="modal-actions">
          <v-spacer></v-spacer>
          <v-btn text @click="closeTransferDialog">Annuler</v-btn>
          <v-btn
            color="primary"
            :disabled="!canSubmitTransfer"
            :loading="transferLoading"
            @click="submitTransfer"
          >
            Transférer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Preloader -->
    <PreloaderComponent :enable="enable"></PreloaderComponent>

    <!-- Content -->
    <div class="content-container" v-if="enable">
      <v-card class="data-card">
        <v-card-title class="card-header">
          <v-text-field
            v-model="search"
            prepend-inner-icon="mdi-magnify"
            placeholder="Rechercher..."
            single-line
            hide-details
            outlined
            dense
            class="table-search"
          ></v-text-field>

          <div class="filter-group">
            <span class="filter-label">Type d'appareil :</span>
            <div class="filter-chips">
              <v-chip
                small
                :outlined="typeFilter !== 'ALL'"
                :color="typeFilter === 'ALL' ? 'primary' : 'grey'"
                class="filter-chip"
                @click="setTypeFilter('ALL')"
              >
                Tous
              </v-chip>
              <v-chip
                v-for="t in availableTypes"
                :key="'type-' + t"
                small
                :outlined="typeFilter !== t"
                :color="getDeviceTypeColor(t)"
                :class="['filter-chip', typeFilter === t ? 'white--text' : '']"
                @click="setTypeFilter(t)"
              >
                <v-icon left x-small>{{ getDeviceTypeIcon(t) }}</v-icon>
                {{ getDeviceTypeLabel(t) }}
              </v-chip>
            </div>
          </div>

          <div class="filter-group">
            <span class="filter-label">Build # :</span>
            <div class="filter-chips">
              <v-chip
                small
                :outlined="buildFilter !== 'ALL'"
                :color="buildFilter === 'ALL' ? 'primary' : 'grey'"
                class="filter-chip"
                @click="setBuildFilter('ALL')"
              >
                Tous
              </v-chip>
              <v-chip
                v-for="b in availableBuilds"
                :key="'build-' + b"
                small
                :outlined="buildFilter !== b"
                color="indigo"
                :class="['filter-chip', buildFilter === b ? 'white--text' : '']"
                @click="setBuildFilter(b)"
              >
                <v-icon left x-small>mdi-package-variant-closed</v-icon>
                {{ b }}
              </v-chip>
            </div>
          </div>

          <v-spacer></v-spacer>
          <div class="export-buttons">
            <v-btn small outlined color="success" class="export-btn" @click="exportExcel" :disabled="!filteredRows.length">
              <v-icon left small>mdi-file-excel</v-icon>
              Excel
            </v-btn>
          </div>
          <v-chip class="results-chip ml-3" small>
            {{ filteredRows.length }} appareils
          </v-chip>
        </v-card-title>

        <div v-if="loading" class="text-caption text--secondary mb-3">
          Chargement des appareils… {{ loadedCount }} chargé(s)
        </div>
        <v-alert v-if="loadError && !loading" dense text type="error" class="mb-2">
          Erreur lors du chargement des appareils. Les appareils déjà chargés restent affichés.
          <a @click.prevent="loadAllDevices()" style="text-decoration: underline; cursor: pointer;">Réessayer</a>
        </v-alert>

        <v-data-table
          :headers="headers"
          :items="filteredRows"
          :items-per-page="10"
          :search="search"
          class="modern-table"
        >
          <template v-slot:item.id="{ item }">
            <span class="id-badge">#{{ item.id }}</span>
          </template>
          <template v-slot:item.name="{ item }">
            <div class="device-name">
              <v-avatar size="32" color="secondary" class="mr-2">
                <v-icon small dark>mdi-cellphone</v-icon>
              </v-avatar>
              {{ item.name }}
            </div>
          </template>
          <template v-slot:item.type="{ item }">
            <v-chip small :color="getDeviceTypeColor(item.type)" class="white--text">
              <v-icon left x-small>{{ getDeviceTypeIcon(item.type) }}</v-icon>
              {{ getDeviceTypeLabel(item.type) }}
            </v-chip>
          </template>
          <template v-slot:item.status="{ item }">
            <v-chip small :class="item.status ? 'status-active' : 'status-inactive'">
              <v-icon left x-small>{{ item.status ? 'mdi-check-circle' : 'mdi-close-circle' }}</v-icon>
              {{ getStatusLabel(item.status) }}
            </v-chip>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn icon small class="action-btn action-btn-view" @click="viewDevice(item)" :loading="loadingDevice === item.id">
              <v-icon small>mdi-eye</v-icon>
            </v-btn>
            <v-btn icon small class="action-btn action-btn-edit" @click="edit(item)">
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card>
    </div>
  </div>
</template>

<script>
import * as XLSX from 'xlsx'
import PreloaderComponent from '../components/PreloaderComponent.vue'
import DeviceFeatureSubscriptionsBlock from '../components/DeviceFeatureSubscriptionsBlock.vue'

var PAGE_SIZE = 200

export default {
  name: 'SubscriptionsView',
  computed: {
    filteredTransferDevices: function() {
      var self = this
      if (!self.selectedDevice || !self.rows || !self.rows.length) return []
      var bid =
        self.selectedDevice.business_id != null
          ? self.selectedDevice.business_id
          : self.selectedDevice.business && self.selectedDevice.business.id
      if (bid == null) return []
      var q = (self.transferDeviceSearch || '').trim().toLowerCase()
      return self.rows.filter(function(d) {
        if (String(d.business_id) !== String(bid)) return false
        if (d.id === self.selectedDevice.id) return false
        if (!q) return true
        var name = (d.name || '').toLowerCase()
        var sn = (d.serial_number || '').toLowerCase()
        var idf = (d.identifier || '').toLowerCase()
        return name.indexOf(q) !== -1 || sn.indexOf(q) !== -1 || idf.indexOf(q) !== -1
      })
    },
    canSubmitTransfer: function() {
      if (!this.transferTargetDevice) return false
      var sn = this.transferTargetDevice.serial_number
      return !!(sn && String(sn).trim().length > 0)
    },
    availableTypes: function() {
      var set = {}
      for (var i = 0; i < this.rows.length; i++) {
        var t = this.rows[i] && this.rows[i].type
        if (t) set[t] = true
      }
      return Object.keys(set).sort()
    },
    availableBuilds: function() {
      var set = {}
      for (var i = 0; i < this.rows.length; i++) {
        var b = this.rows[i] && this.rows[i].os_version
        if (b !== null && b !== undefined && String(b).trim() !== '') {
          set[String(b)] = true
        }
      }
      return Object.keys(set).sort()
    },
    filteredRows: function() {
      var self = this
      var result = this.rows
      if (this.typeFilter !== 'ALL') {
        result = result.filter(function(row) {
          return row && row.type === self.typeFilter
        })
      }
      if (this.buildFilter !== 'ALL') {
        result = result.filter(function(row) {
          return row && String(row.os_version) === self.buildFilter
        })
      }
      return result
    }
  },
  methods: {
    async loadAllDevices() {
      var gen = ++this.generation
      this.rows = []
      this.loadedCount = 0
      this.loading = true
      this.loadError = false
      var skip = 0
      try {
        for (;;) {
          var r = await this.$axios.get('/devices?include=business,store&orderasc=id&skip=' + skip + '&limit=' + PAGE_SIZE)
          if (gen !== this.generation) return
          var raw = r.data || []
          if (!Array.isArray(raw)) { this.loadError = true; break }
          if (raw.length > PAGE_SIZE || skip > 100000) { this.loadError = true; break }
          var visible = raw
          // Si l'utilisateur est un agent (role_id === 2), filtrer par seller_id du business
          var user = this.$store.getters.StateUser
          if (user && user.role_id === 2) {
            visible = raw.filter(function(device) {
              return device.business && device.business.seller_id === user.id
            })
          }
          this.rows = this.rows.concat(visible)
          this.loadedCount = this.rows.length
          // Afficher le tableau dès la première page reçue
          this.enable = true
          // La fin de pagination se juge sur la page brute, jamais sur la page filtrée
          if (raw.length < PAGE_SIZE) break
          skip += PAGE_SIZE
        }
      } catch (e) {
        if (gen !== this.generation) return
        this.loadError = true
      }
      this.loading = false
      this.enable = true
    },
    getStatusColor: function(status){
        var color = "class_red"
        if(status){
          color = "class_green"
        }
        return color
      },
      getBusinesses: function(){
        var self = this;
        this.$axios.get('/businesses')
        .then(function (response) {
          self.businesses = response.data
        })
      },
      getStores: function(){
        var self = this;
        this.$axios.get('/stores')
        .then(function (response) {
          self.all_stores = response.data
        })
      },
      getStatusLabel: function(status){
        var label = "Annulé"
        if(status){
          label = "Actif"
        }
        return label
      },
      setTypeFilter: function(type){
        this.typeFilter = type
      },
      setBuildFilter: function(build){
        this.buildFilter = build
      },
      getDeviceTypeLabel: function(type){
        if (!type) return 'Inconnu'
        var labels = {
          'phone': 'Téléphone',
          'tablet': 'Tablette',
          'windows': 'Windows',
          'ios': 'iOS',
          'linux': 'Linux',
          'kiosk': 'Kiosk',
          'web': 'Web',
          'other': 'Autre'
        }
        return labels[type] || type
      },
      getDeviceTypeColor: function(type){
        if (!type) return 'grey'
        var colors = {
          'phone': '#2196F3',
          'tablet': '#9C27B0',
          'windows': '#00BCD4',
          'ios': '#607D8B',
          'linux': '#FF5722',
          'kiosk': '#795548',
          'web': '#4CAF50',
          'other': '#9E9E9E'
        }
        return colors[type] || 'grey'
      },
      getDeviceTypeIcon: function(type){
        if (!type) return 'mdi-help-circle'
        var icons = {
          'phone': 'mdi-cellphone',
          'tablet': 'mdi-tablet',
          'windows': 'mdi-microsoft-windows',
          'ios': 'mdi-apple',
          'linux': 'mdi-linux',
          'kiosk': 'mdi-monitor',
          'web': 'mdi-web',
          'other': 'mdi-devices'
        }
        return icons[type] || 'mdi-devices'
      },
      setBusinessStores:function(business_id){
        this.stores = []
        for (var i = this.all_stores.length - 1; i >= 0; i--) {
          if(this.all_stores[i].business_id == business_id){
            this.stores.push(this.all_stores[i])
          }
        }
      },
      getAmount: function(amount){
        return amount
      },
      save: function(){
        this.loading = true
        var self = this
        var url = ''
        if (this.index > -1) { 

          url = '/devices/'+self.device.id
          this.$axios.put(url+"?include=business,store&data="+encodeURIComponent(JSON.stringify(self.device)))
          .then(response => {
            Object.assign(self.rows[self.index], response.data)
            self.dialog = false
            self.loading = false
            self.reset()
          })
        }
      },
      edit: function(item){
        this.setBusinessStores(item.business_id)
        this.index = this.rows.indexOf(item)
        this.device = Object.assign({}, item)
        this.form_title = "Editer Appareil : "+this.device.name
        this.dialog = true
      },
      close: function(){
        this.dialog = false
        this.reset()
        this.loading = false
      },
      reset: function(){
        this.device = {
          name: '', 
          manufacturer: '', 
          model: '', 
          serial_number: '',
          status: 1,
        }
        this.index = -1
        this.form_title = 'Nouvel Appareil'
      },
      closeDelete () {
        this.dialogDelete = false
        this.$nextTick(() => {
          this.reset()
        })
      },
      viewDevice: function(item) {
        var self = this
        this.loadingDevice = item.id
        this.$axios.get('/devices/' + item.id + '?include=business,subscriptions.payment_request,subscriptions.plan')
          .then(function(response) {
            self.selectedDevice = response.data
            self.detailsDialog = true
          })
          .catch(function() {
            alert('Erreur lors du chargement des détails')
          })
          .finally(function() {
            self.loadingDevice = null
          })
      },
      onFeatureSubscriptionTransferred: function() {
        var self = this
        if (!self.selectedDevice || !self.selectedDevice.id) return
        var row = self.rows.find(function(r) { return r.id === self.selectedDevice.id })
        if (row) {
          self.viewDevice(row)
        }
      },
      formatDate: function(date) {
        if (!date) return '-'
        var d = new Date(date)
        var day = d.getDate().toString().padStart(2, '0')
        var month = (d.getMonth() + 1).toString().padStart(2, '0')
        var year = d.getFullYear()
        return day + '/' + month + '/' + year
      },
      formatDateTime: function(date) {
        if (!date) return '-'
        var d = new Date(date)
        var day = d.getDate().toString().padStart(2, '0')
        var month = (d.getMonth() + 1).toString().padStart(2, '0')
        var year = d.getFullYear()
        var hours = d.getHours().toString().padStart(2, '0')
        var minutes = d.getMinutes().toString().padStart(2, '0')
        return day + '/' + month + '/' + year + ' ' + hours + ':' + minutes
      },
      formatNumber: function(value) {
        if (!value && value !== 0) return '0'
        return parseInt(value).toLocaleString('fr-FR')
      },
      getSubscriptionStatusColor: function(status) {
        if (!status) return 'grey'
        var s = status.toUpperCase()
        if (s === 'ACTIVE') return 'success'
        if (s === 'SCHEDULED') return 'info'
        if (s === 'EXPIRED') return 'warning'
        if (s === 'CANCELLED') return 'error'
        return 'grey'
      },
      getPaymentStatusColor: function(status) {
        if (!status) return 'grey'
        var s = status.toUpperCase()
        if (s === 'CONFIRMED') return 'success'
        if (s === 'PENDING') return 'warning'
        if (s === 'DENIED') return 'error'
        return 'grey'
      },
      getProcessorColor: function(processor) {
        if (!processor) return 'grey'
        var p = processor.toUpperCase()
        if (p === 'MONCASH') return '#E91E63'
        if (p === 'NATCASH') return '#FF9800'
        if (p === 'CASH') return '#4CAF50'
        return 'grey'
      },
      getSubPaymentStatus: function(sub) {
        if (sub && sub.payment_request && sub.payment_request.status) {
          return sub.payment_request.status
        }
        return '-'
      },
      isSubscriptionActive: function(sub) {
        if (!sub || !sub.status) return false
        return String(sub.status).toUpperCase() === 'ACTIVE'
      },
      openTransferDialog: function(sub) {
        this.transferSubscription = sub
        this.transferDeviceSearch = ''
        this.transferTargetDevice = null
        this.transferDialog = true
      },
      closeTransferDialog: function() {
        this.transferDialog = false
        this.transferSubscription = null
        this.transferDeviceSearch = ''
        this.transferTargetDevice = null
        this.transferLoading = false
      },
      submitTransfer: function() {
        var self = this
        if (!self.canSubmitTransfer || !self.transferSubscription) return
        var subId = self.transferSubscription.id
        if (subId == null) {
          alert('Souscription invalide (id manquant).')
          return
        }
        var serial = String(self.transferTargetDevice.serial_number).trim()
        self.transferLoading = true
        self.$axios
          .post('/subscriptions/transfer', {
            subscription_id: subId,
            serial_number: serial
          })
          .then(function(response) {
            var data = response.data
            if (data && data.success) {
              self.closeTransferDialog()
              self.detailsDialog = false
              self.selectedDevice = null
              window.location.reload()
            } else {
              var msg =
                (data && (data.message || data.error)) ||
                'Le transfert a échoué.'
              alert(msg)
            }
          })
          .catch(function(err) {
            var msg = 'Erreur lors du transfert.'
            if (err.response && err.response.data) {
              var d = err.response.data
              msg = d.message || d.error || msg
            }
            alert(msg)
          })
          .finally(function() {
            self.transferLoading = false
          })
      },
      exportExcel: function() {
        var self = this
        var data = self.filteredRows.map(function(row) {
          return {
            'ID': row.id || '',
            'Nom': row.name || '',
            'Compte': row.business && row.business.name ? row.business.name : '',
            'Numéro de série': row.serial_number || '',
            'Identifiant': row.identifier || '',
            'Build #': row.os_version || '',
            'Type': self.getDeviceTypeLabel(row.type),
            'Fabricant': row.manufacturer || '',
            'Modèle': row.model || '',
            'Opérateur': row.operator || '',
            'Adresse IP': row.ip_address || '',
            'Adresse MAC': row.mac_address || '',
            'IMEI': row.imei || '',
            'Statut': self.getStatusLabel(row.status),
            'Date création': self.formatDateTime(row.created_at)
          }
        })
        var ws = XLSX.utils.json_to_sheet(data)
        var wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, 'Appareils')
        var today = new Date()
        var stamp = today.getFullYear() + '-' +
          String(today.getMonth() + 1).padStart(2, '0') + '-' +
          String(today.getDate()).padStart(2, '0')
        XLSX.writeFile(wb, 'appareils_' + stamp + '.xlsx')
      },
      verifyPayment: function(sub) {
        var self = this
        if (!sub || !sub.payment_request || !sub.payment_request.id) return
        
        var paymentId = sub.payment_request.id
        this.$set(this.verifyingPayments, paymentId, true)
        
        this.$axios.get('/payment-requests/' + paymentId)
          .then(function(response) {
            var updatedPayment = response.data
            if (updatedPayment && self.selectedDevice && self.selectedDevice.subscriptions) {
              for (var i = 0; i < self.selectedDevice.subscriptions.length; i++) {
                if (self.selectedDevice.subscriptions[i].payment_request && 
                    self.selectedDevice.subscriptions[i].payment_request.id === paymentId) {
                  self.$set(self.selectedDevice.subscriptions[i], 'payment_request', updatedPayment)
                  break
                }
              }
            }
          })
          .catch(function() {
            alert('Erreur lors de la vérification du paiement')
          })
          .finally(function() {
            self.$set(self.verifyingPayments, paymentId, false)
          })
      }
  },
  mounted(){
    this.loadAllDevices()
    this.getBusinesses()
    this.getStores()
  },
  beforeDestroy() {
    this.generation++
  },
  data: () => ({
      menu: false,
      modal: false,
      menu2: false,
      search: '',
      typeFilter: 'ALL',
      buildFilter: 'ALL',
      status: [{name: "Active", id: true}, {name: 'Blocked', id: false}],
      index: -1,
      loading: false,
      generation: 0,
      loadError: false,
      loadedCount: 0,
      form_title: "Nouvel Appareil",
      items: [
        {
          text: 'Appareils',
          disabled: false,
          href: '/subscriptions',
        }
      ],
      fluid: true,
      enable: false,
      headers: [
          {
            text: '#',
            align: 'start',
            sortable: true,
            value: 'id',
          },
          {
            text: 'NOM',
            align: 'start',
            sortable: false,
            value: 'name',
          },
          { text: 'COMPTE', value: 'business.name' },
          { text: 'SN', value: 'serial_number' },
          { text: 'IDENTIFIANT', value: 'identifier' },
          { text: 'BUILD #', value: 'os_version' },
          { text: 'TYPE', value: 'type' },
          { text: 'STATUT', value: 'status' },
          { text: 'ACTIONS', value: 'actions' },
        ],
        rows: [],
        businesses: [],
        all_stores: [],
        stores: [],
        dialog:'',
        dialogDelete: false,
        detailsDialog: false,
        selectedDevice: null,
        loadingDevice: null,
        verifyingPayments: {},
        transferDialog: false,
        transferSubscription: null,
        transferDeviceSearch: '',
        transferTargetDevice: null,
        transferLoading: false,
        device: {
          name: '', 
          manufacturer: '', 
          model: '', 
          serial_number: '',
          status: 1,
        }
    }),
  components: { PreloaderComponent, DeviceFeatureSubscriptionsBlock },
}
</script>

<style lang="scss" scoped>
.subscriptions-view {
  min-height: 100%;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  
  .page-title {
    font-size: 24px;
    font-weight: 600;
    margin: 0;
  }
  
  .page-subtitle {
    font-size: 14px;
    color: rgba(0, 0, 0, 0.6);
    margin: 4px 0 0;
  }
}

.theme--dark .page-subtitle {
  color: rgba(255, 255, 255, 0.6);
}

.content-container {
  padding: 0 24px 24px;
}

.data-card {
  border-radius: 12px !important;
  overflow: hidden;
}

.card-header {
  padding: 16px 20px !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.table-search {
  max-width: 300px;
  
  ::v-deep .v-input__slot {
    min-height: 40px !important;
    border-radius: 8px !important;
  }
}

.results-chip {
  font-weight: 500;
}

.export-buttons {
  display: flex;
  gap: 8px;
}

.export-btn {
  text-transform: none !important;
  font-weight: 500 !important;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-left: 12px;
}

.filter-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(0, 0, 0, 0.55);
}

.theme--dark .filter-label {
  color: rgba(255, 255, 255, 0.6);
}

.filter-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.filter-chip {
  cursor: pointer;
  font-weight: 500 !important;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  }
}

.device-name {
  display: flex;
  align-items: center;
  font-weight: 500;
}

.id-badge {
  font-weight: 600;
  color: #003F74;
  background: rgba(0, 63, 116, 0.08);
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 13px;
}

.theme--dark .id-badge {
  color: #4A90D9;
  background: rgba(74, 144, 217, 0.15);
}

.status-active {
  background: rgba(76, 175, 80, 0.15) !important;
  color: #2E7D32 !important;
}

.status-inactive {
  background: rgba(255, 82, 82, 0.15) !important;
  color: #D32F2F !important;
}

.action-btn {
  width: 32px !important;
  height: 32px !important;
  margin: 0 4px !important;
}

.action-btn-view {
  background: rgba(76, 175, 80, 0.1) !important;
  color: #4CAF50 !important;
}

.action-btn-edit {
  background: rgba(0, 63, 116, 0.1) !important;
  color: #003F74 !important;
}

.dialog-title {
  font-size: 18px !important;
  font-weight: 600 !important;
  padding: 20px 24px !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.dialog-content {
  padding: 24px !important;
  max-height: 60vh;
}

.dialog-actions {
  padding: 16px 24px !important;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.cancel-btn {
  text-transform: none !important;
}

.save-btn {
  text-transform: none !important;
  min-width: 120px !important;
}

// Details Modal
.details-modal {
  border-radius: 12px !important;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.modal-title {
  font-size: 18px;
  font-weight: 600;
  display: flex;
  align-items: center;
}

.modal-content {
  padding: 24px !important;
  max-height: 70vh;
}

.modal-actions {
  padding: 16px 24px !important;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.detail-section {
  margin-bottom: 24px;
  padding: 16px;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 8px;
  
  &:last-child {
    margin-bottom: 0;
  }
}

.theme--dark .detail-section {
  background: rgba(255, 255, 255, 0.05);
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  color: #003F74;
}

.theme--dark .section-title {
  color: #4A90D9;
}

.subsection-title {
  font-size: 14px;
  font-weight: 600;
  color: #666;
  margin-bottom: 12px;
}

.theme--dark .subsection-title {
  color: #aaa;
}

.detail-grid {
  margin: 0 -8px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.detail-label {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.theme--dark .detail-label {
  color: rgba(255, 255, 255, 0.5);
}

.detail-value {
  font-size: 14px;
  font-weight: 500;
}

.amount-value {
  color: #003F74;
  font-weight: 600;
}

.theme--dark .amount-value {
  color: #4A90D9;
}

.subscriptions-list {
  margin-top: 8px;
}

.subscription-header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
}

.sub-plan {
  font-weight: 500;
  flex: 1;
}

.sub-dates {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.6);
}

.theme--dark .sub-dates {
  color: rgba(255, 255, 255, 0.6);
}

.no-data {
  color: rgba(0, 0, 0, 0.5);
  font-style: italic;
  text-align: center;
  padding: 20px;
}

.theme--dark .no-data {
  color: rgba(255, 255, 255, 0.5);
}

.payment-status-cell {
  display: flex;
  align-items: center;
  gap: 4px;
}

.verify-btn {
  width: 20px !important;
  height: 20px !important;
  background: rgba(0, 63, 116, 0.1) !important;
  
  &:hover {
    background: rgba(0, 63, 116, 0.2) !important;
  }
}

.theme--dark .verify-btn {
  background: rgba(74, 144, 217, 0.2) !important;
  
  &:hover {
    background: rgba(74, 144, 217, 0.3) !important;
  }
}

.transfer-action-col {
  margin-top: 4px;
}

.transfer-btn {
  text-transform: none !important;
}

::v-deep .transfer-subscription-dialog {
  z-index: 300 !important;
}

.transfer-modal-header {
  flex-wrap: nowrap;
}

.transfer-modal-body {
  padding: 16px 24px 8px !important;
  max-height: 60vh;
}

.transfer-origin {
  margin-bottom: 16px;
}

.transfer-device-list {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  max-height: 240px;
  overflow-y: auto;
}

.theme--dark .transfer-device-list {
  border-color: rgba(255, 255, 255, 0.12);
}

.transfer-device-selected {
  background: rgba(0, 63, 116, 0.08) !important;
}

.theme--dark .transfer-device-selected {
  background: rgba(74, 144, 217, 0.15) !important;
}

// Responsive Styles
@media (max-width: 599px) {
  .page-header {
    padding: 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    
    .page-title {
      font-size: 20px;
    }
    
    .page-subtitle {
      font-size: 12px;
    }
  }
  
  .content-container {
    padding: 0 12px 12px;
  }
  
  .card-header {
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 12px !important;
    padding: 12px !important;
  }
  
  .table-search {
    max-width: 100% !important;
  }

  .filter-group {
    margin-left: 0 !important;
    width: 100%;
  }

  .filter-chips {
    .filter-chip {
      font-size: 10px !important;
      height: 26px !important;
    }
  }

  .device-name {
    font-size: 13px;
    
    .v-avatar {
      width: 28px !important;
      height: 28px !important;
    }
  }
  
  .details-modal {
    .modal-header {
      padding: 12px 16px !important;
    }
    
    .modal-title {
      font-size: 16px;
    }
    
    .modal-content {
      padding: 16px !important;
      max-height: 80vh;
    }
    
    .detail-section {
      padding: 12px;
      margin-bottom: 16px;
    }
    
    .section-title {
      font-size: 14px;
      margin-bottom: 12px;
    }
    
    .detail-label {
      font-size: 10px;
    }
    
    .detail-value {
      font-size: 13px;
    }
  }
  
  .subscription-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    
    .sub-dates {
      font-size: 11px;
    }
  }
  
  .subsection-title {
    font-size: 12px;
  }
}

@media (min-width: 600px) and (max-width: 959px) {
  .page-header {
    padding: 20px;
    
    .page-title {
      font-size: 22px;
    }
  }
  
  .content-container {
    padding: 0 16px 16px;
  }
  
  .card-header {
    flex-wrap: wrap;
    gap: 12px !important;
  }
  
  .details-modal {
    .modal-content {
      max-height: 75vh;
    }
  }
}
</style>